<template>
  <UiIconButton
    v-if="isCollapsible && isCollapsed"
    v-bind="$attrs"
    type="button"
    class="ui-callout__collapsed-trigger"
    variant="secondary"
    size="sm"
    :aria-label="expandLabel"
    :aria-controls="contentId"
    :aria-expanded="false"
    @click="expand"
  >
    <BookText
      :size="18"
      :stroke-width="1.9"
      aria-hidden="true"
    />
  </UiIconButton>

  <UiAlert
    v-else
    v-bind="$attrs"
    :id="contentId"
    class="ui-callout"
    :class="isCollapsible && 'ui-callout--collapsible'"
    :tone="tone"
    :closable="closable || isCollapsible"
    :close-label="isCollapsible ? collapseLabel : closeLabel"
    :compact="compact"
    @close="handleClose"
  >
    <template #icon>
      <MessageCircleWarning
        class="ui-callout__icon"
        :size="18"
        :stroke-width="1.9"
        aria-hidden="true"
      />
    </template>

    <template
      v-if="title || $slots.title"
      #title
    >
      <slot name="title">
        {{ title }}
      </slot>
    </template>
    <slot />
  </UiAlert>
</template>

<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { BookText, MessageCircleWarning } from 'lucide-vue-next';
import UiAlert from './UiAlert.vue';
import UiIconButton from './UiIconButton.vue';

defineOptions({ inheritAttrs: false });

type Tone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

const props = withDefaults(
  defineProps<{
    title?: string;
    tone?: Tone;
    closable?: boolean;
    collapsible?: boolean;
    compact?: boolean;
    collapsed?: boolean;
    defaultCollapsed?: boolean;
    expandLabel?: string;
    collapseLabel?: string;
    closeLabel?: string;
  }>(),
  {
    title: undefined,
    tone: 'info',
    closable: false,
    collapsible: false,
    compact: false,
    collapsed: undefined,
    defaultCollapsed: false,
    expandLabel: 'Показать подсказку',
    collapseLabel: 'Свернуть подсказку',
    closeLabel: 'Закрыть подсказку'
  }
);

const emit = defineEmits<{
  close: [];
  'update:collapsed': [value: boolean];
}>();

const generatedId = useId();
const internalCollapsed = ref(props.defaultCollapsed);

watch(
  () => props.defaultCollapsed,
  (value) => {
    if (props.collapsed === undefined) {
      internalCollapsed.value = value;
    }
  }
);

const contentId = computed(() => `ui-callout-${generatedId}`);
const isCollapsible = computed(() => props.collapsible);
const isCollapsed = computed(() => (props.collapsed === undefined ? internalCollapsed.value : props.collapsed));

const setCollapsed = (value: boolean) => {
  if (props.collapsed === undefined) {
    internalCollapsed.value = value;
  }
  emit('update:collapsed', value);
};

const expand = () => {
  setCollapsed(false);
};

const handleClose = () => {
  if (isCollapsible.value) {
    setCollapsed(true);
    return;
  }

  emit('close');
};
</script>

<style scoped>
.ui-callout {
  width: 100%;
}

.ui-callout__icon {
  flex: 0 0 auto;
}

.ui-callout__collapsed-trigger {
  --ui-icon-button-bg: color-mix(in srgb, var(--ui-info) 10%, var(--ui-surface) 90%);
  --ui-icon-button-border: color-mix(in srgb, var(--ui-info) 45%, var(--ui-border) 55%);
  --ui-icon-button-color: color-mix(in srgb, var(--ui-foreground) 70%, var(--ui-info) 30%);

  flex: 0 0 auto;
}

.ui-callout--collapsible :deep(.ui-alert__close) {
  color: var(--ui-foreground-muted);
}
</style>
